import React, { useState, useEffect } from 'react';
import {
  FiPieChart,
  FiBox,
  FiFileText,
  FiShoppingCart,
  FiUsers,
  FiShield,
  FiChevronDown,
  FiChevronUp,
  FiHome,
  FiLogOut,
  FiRefreshCw,
  FiTrendingUp,
  FiPlus,
  FiEdit2,
  FiTrash2,
  FiEye,
  FiX,
  FiCheckCircle,
  FiXCircle,
  FiSettings
} from 'react-icons/fi';
import axios from 'axios';
import { API_BASE_URL } from '../../config';
import './AdminDashboard.css';
import ProductFormModal from './ProductFormModal';
import BlogFormModal from './BlogFormModal';
import OrderItemsModal from './OrderItemsModal';
import OrderDetailsModal from './OrderDetailsModal';

const fallbackProducts = [
  { id: 1, title: "Super Novel" },
  { id: 2, title: "Prime" },
  { id: 3, title: "Plus" },
  { id: 4, title: "Organic Manure" },
  { id: 5, title: "Vermicompost" },
  { id: 6, title: "Enriched Organic Manure" },
  { id: 7, title: "Super Novel (200ml)" },
  { id: 8, title: "Super Plus (200ml)" },
  { id: 9, title: "Super (200ml)" },
  { id: 10, title: "Vermicompost (200ml)" },
  { id: 11, title: "Enriched Organic Manure (200ml)" },
  { id: 12, title: "Plus (200ml)" },
  { id: 13, title: "Organic Manure (200ml)" }
];

export default function AdminDashboard({ currentUser, onLogout, onBack, triggerNotification, onStorefrontLayoutSaved }) {
  const [activeTab, setActiveTab] = useState('overview'); // 'overview' | 'products' | 'blogs' | 'orders' | 'users' | 'logs' | 'settings' | 'landing'
  const [loading, setLoading] = useState(false);
  const [profileDropdownOpen, setProfileDropdownOpen] = useState(false);

  // Lists from backend
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [blogs, setBlogs] = useState([]);
  const [orders, setOrders] = useState([]);
  const [users, setUsers] = useState([]);
  const [logs, setLogs] = useState([]);
  const [orderSearchQuery, setOrderSearchQuery] = useState('');

  // Modals & trigger states
  const [productFormOpen, setProductFormOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [blogFormOpen, setBlogFormOpen] = useState(false);
  const [editingBlog, setEditingBlog] = useState(null);
  const [selectedOrderItems, setSelectedOrderItems] = useState(null);
  const [selectedAdminOrder, setSelectedAdminOrder] = useState(null);

  // Storefront Designer state
  const [landingForm, setLandingForm] = useState({
    topProducts: [1, 2, 3, 4],
    spotlightProducts: [5, 6, 4]
  });

  // Store Settings state
  const [settingsForm, setSettingsForm] = useState({
    shipping_fee: '50',
    free_shipping_threshold: '500',
    tax_rate: '5',
    invoice_company_name: 'Orbifert Organic Pvt. Ltd.',
    invoice_company_address: 'ALYSSA, BEGONIA AND CLOVER, EMBASSY TECH VILLAGE, OUTER RING ROAD, BENGALURU, Karnataka, 560103',
    invoice_gstin: '29AABCF8078M1ZX',
    invoice_pan: 'AABCF8078M',
    invoice_cin: 'U51909KA2011PTC060489',
    invoice_contact_phone: '+91 7228064418',
    invoice_contact_email: 'billing@orbifertorganic.com'
  });

  const [settingsSubTab, setSettingsSubTab] = useState('store'); // 'store' | 'invoice'

  const API_URL = API_BASE_URL;
  const token = localStorage.getItem('token');

  // Fetch all dashboard data
  const fetchData = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const headers = { 'Authorization': `Bearer ${token}` };

      // Categories (Public)
      try {
        const catRes = await axios.get(`${API_URL}/categories?t=${Date.now()}`);
        setCategories(catRes.data.categories || []);
      } catch (err) {
        console.error('Failed to load categories:', err);
        // Fallback defaults
        setCategories([
          { id: 1, name: 'Fertilizers' },
          { id: 2, name: 'Bio Fertilizers' },
          { id: 3, name: 'Soil Conditioners' },
          { id: 4, name: 'Plant Growth Promoters' },
          { id: 5, name: 'Pesticides & Fungicides' }
        ]);
      }

      // Products (Public)
      try {
        const prodRes = await axios.get(`${API_URL}/products?t=${Date.now()}`);
        setProducts(prodRes.data.products || []);
      } catch (err) {
        console.error('Failed to load products:', err);
      }

      // Blogs (Public)
      try {
        const blogRes = await axios.get(`${API_URL}/blogs?t=${Date.now()}`);
        setBlogs(blogRes.data.blogs || []);
      } catch (err) {
        console.error('Failed to load blogs:', err);
      }

      // Orders (Admin)
      try {
        const orderRes = await axios.get(`${API_URL}/orders/admin/all?t=${Date.now()}`, { headers });
        setOrders(orderRes.data.orders || []);
      } catch (err) {
        console.error('Failed to load orders:', err);
      }

      // Users (Master Admin)
      try {
        const usersRes = await axios.get(`${API_URL}/auth/admin/users?t=${Date.now()}`, { headers });
        setUsers(usersRes.data.users || []);
      } catch (err) {
        console.error('Failed to load users:', err);
      }

      // Logs (Master Admin)
      try {
        const logsRes = await axios.get(`${API_URL}/auth/admin/logs?t=${Date.now()}`, { headers });
        setLogs(logsRes.data.logs || []);
      } catch (err) {
        console.error('Failed to load logs:', err);
      }

      // Store Settings
      try {
        const settingsRes = await axios.get(`${API_URL}/orders/settings/all?t=${Date.now()}`);
        if (settingsRes.data.settings) {
          setSettingsForm({
            shipping_fee: settingsRes.data.settings.shipping_fee || '50',
            free_shipping_threshold: settingsRes.data.settings.free_shipping_threshold || '500',
            tax_rate: settingsRes.data.settings.tax_rate || '5',
            invoice_company_name: settingsRes.data.settings.invoice_company_name || 'Orbifert Organic Pvt. Ltd.',
            invoice_company_address: settingsRes.data.settings.invoice_company_address || '',
            invoice_gstin: settingsRes.data.settings.invoice_gstin || '',
            invoice_pan: settingsRes.data.settings.invoice_pan || '',
            invoice_cin: settingsRes.data.settings.invoice_cin || '',
            invoice_contact_phone: settingsRes.data.settings.invoice_contact_phone || '',
            invoice_contact_email: settingsRes.data.settings.invoice_contact_email || ''
          });
        }
      } catch (err) {
        console.error('Failed to load settings:', err);
      }

    } catch (err) {
      console.error('Failed to load dashboard data:', err);
      triggerNotification('Failed to synchronize data with backend server.');
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  useEffect(() => {
    // Load storefront layout from the DB (authoritative global source)
    const loadStorefront = async () => {
      try {
        const res = await axios.get(`${API_URL}/orders/storefront/layout?t=${Date.now()}`);
        if (res.data) {
          setLandingForm({
            topProducts: res.data.top_products || [1, 2, 3, 4],
            spotlightProducts: res.data.spotlight_products || [5, 6, 4]
          });
        }
      } catch (err) {
        // Fallback to localStorage if API unavailable
        const savedTop = localStorage.getItem('landing_top_products');
        const savedSpotlight = localStorage.getItem('landing_spotlight_products');
        setLandingForm({
          topProducts: savedTop ? JSON.parse(savedTop) : [1, 2, 3, 4],
          spotlightProducts: savedSpotlight ? JSON.parse(savedSpotlight) : [5, 6, 4]
        });
      }
    };
    loadStorefront();
  }, []);

  const handleTopProductChange = (index, value) => {
    const updated = [...landingForm.topProducts];
    updated[index] = parseInt(value) || value;
    setLandingForm({ ...landingForm, topProducts: updated });
  };

  const handleSpotlightProductChange = (index, value) => {
    const updated = [...landingForm.spotlightProducts];
    updated[index] = parseInt(value) || value;
    setLandingForm({ ...landingForm, spotlightProducts: updated });
  };

  const handleLandingDesignerSubmit = async (e) => {
    e.preventDefault();
    const topIds = landingForm.topProducts.map(v => parseInt(v));
    const spotIds = landingForm.spotlightProducts.map(v => parseInt(v));

    try {
      // Save to DB — this is the single source of truth for all devices
      await axios.put(
        `${API_URL}/orders/storefront/layout`,
        { top_products: topIds, spotlight_products: spotIds },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      // Mirror to localStorage as a local cache for instant UI update on same device
      localStorage.setItem('landing_top_products', JSON.stringify(topIds));
      localStorage.setItem('landing_spotlight_products', JSON.stringify(spotIds));
      // Notify landing page to re-render immediately
      if (typeof onStorefrontLayoutSaved === 'function') {
        onStorefrontLayoutSaved(topIds, spotIds);
      }
      triggerNotification('✅ Storefront layout saved to database! All devices will reflect this change.');
    } catch (err) {
      console.error('Failed to save storefront layout to DB:', err);
      triggerNotification('❌ Failed to save storefront layout. Check your connection and try again.');
    }
  };

  useEffect(() => {
    const eventSource = new EventSource(`${API_URL}/orders/realtime`);

    eventSource.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.type === 'ORDER_PLACED') {
          triggerNotification('🔔 Realtime: New order received!');
          fetchData(true);
        } else if (data.type === 'TRACKING_UPDATED') {
          triggerNotification(`📦 Realtime: Order #${data.orderId} status updated to ${data.status || 'new status'}!`);
          fetchData(true);
        }
      } catch (err) {
        console.error('Failed to parse SSE message:', err);
      }
    };

    eventSource.onerror = (err) => {
      console.warn('Real-time connection dropped. Retrying...', err);
    };

    return () => {
      eventSource.close();
    };
  }, []);

  useEffect(() => {
    const clickOutsideProfile = (e) => {
      if (!e.target.closest('.admin-profile-container')) {
        setProfileDropdownOpen(false);
      }
    };
    document.addEventListener('click', clickOutsideProfile);
    return () => document.removeEventListener('click', clickOutsideProfile);
  }, []);

  // Product CRUD Handlers
  // Product CRUD Handlers
  const handleOpenAddProduct = () => {
    setEditingProduct(null);
    setProductFormOpen(true);
  };

  const handleOpenEditProduct = (prod) => {
    setEditingProduct(prod);
    setProductFormOpen(true);
  };

  const handleDeleteProduct = async (id) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;
    try {
      await axios.delete(`${API_URL}/products/${id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      triggerNotification('Product removed from catalog.');
      fetchData();
    } catch (err) {
      console.error(err);
      const errorMessage = err.response?.data?.message || 'Connection error.';
      triggerNotification(errorMessage);
    }
  };

  // Blog CRUD Handlers
  const handleOpenAddBlog = () => {
    setEditingBlog(null);
    setBlogFormOpen(true);
  };

  const handleOpenEditBlog = (blog) => {
    setEditingBlog(blog);
    setBlogFormOpen(true);
  };

  const handleDeleteBlog = async (id) => {
    if (!window.confirm('Delete this blog post?')) return;
    try {
      await axios.delete(`${API_URL}/blogs/${id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      triggerNotification('Blog post deleted.');
      fetchData();
    } catch (err) {
      console.error(err);
      const errorMessage = err.response?.data?.message || 'Connection error.';
      triggerNotification(errorMessage);
    }
  };

  const handleUpdateOrderDetails = async (orderId, updates) => {
    try {
      await axios.put(`${API_URL}/orders/${orderId}/status`, updates, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      triggerNotification('Order updated successfully.');

      // Refetch and update local state
      const headers = { 'Authorization': `Bearer ${token}` };
      const orderRes = await axios.get(`${API_URL}/orders/admin/all?t=${Date.now()}`, { headers });
      const updatedOrders = orderRes.data.orders || [];
      setOrders(updatedOrders);

      // Update selected order in modal
      const refreshedOrder = updatedOrders.find(o => o.id === orderId);
      if (refreshedOrder) {
        setSelectedAdminOrder(refreshedOrder);
      } else {
        setSelectedAdminOrder(null);
      }
    } catch (err) {
      console.error(err);
      const errorMessage = err.response?.data?.message || 'Connection error.';
      triggerNotification(errorMessage);
    }
  };

  // User Management Handlers
  const handleToggleUserStatus = async (userId, currentActive) => {
    try {
      await axios.put(`${API_URL}/auth/admin/users/${userId}`, { is_active: currentActive ? 0 : 1 }, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      triggerNotification(currentActive ? 'Account deactivated.' : 'Account activated.');
      fetchData();
    } catch (err) {
      console.error(err);
      const errorMessage = err.response?.data?.message || 'Connection error.';
      triggerNotification(errorMessage);
    }
  };

  const handleRoleChange = async (userId, newRole) => {
    try {
      await axios.put(`${API_URL}/auth/admin/users/${userId}`, { role: newRole }, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      triggerNotification(`User role updated to: ${newRole}`);
      fetchData();
    } catch (err) {
      console.error(err);
      const errorMessage = err.response?.data?.message || 'Connection error.';
      triggerNotification(errorMessage);
    }
  };

  const handleSettingsSubmit = async (e) => {
    e.preventDefault();
    try {
      const headers = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      };
      await axios.put(`${API_URL}/orders/settings/update`, settingsForm, { headers });
      triggerNotification('Store settings updated successfully.');
      fetchData();
    } catch (err) {
      console.error(err);
      const errorMessage = err.response?.data?.message || 'Failed to update store settings.';
      triggerNotification(errorMessage);
    }
  };

  // Calculations for Overview Screen
  const totalRevenue = orders
    .filter(o => o.status === 'delivered')
    .reduce((sum, o) => sum + parseFloat(o.total_amount || 0), 0);

  const pendingOrders = orders.filter(o => o.status === 'pending').length;

  return (
    <div className="admin-portal-container">
      {/* Sidebar Navigation */}
      <aside className="admin-sidebar">
        <div className="sidebar-brand">
          <div className="admin-brand-logo-wrapper">
            <img src="/logo/Orbifert Logo.png" orbifert-logo alt="Orbifert Logo" className="admin-brand-logo" />
            <span className="admin-brand-sub">Master Admin Panel</span>
          </div>
        </div>

        <nav className="sidebar-nav">
          <button
            className={`nav-link-btn ${activeTab === 'overview' ? 'active' : ''}`}
            onClick={() => setActiveTab('overview')}
          >
            <span className="nav-btn-content">
              <FiPieChart className="nav-icon" /> Overview
            </span>
          </button>
          <button
            className={`nav-link-btn ${activeTab === 'products' ? 'active' : ''}`}
            onClick={() => setActiveTab('products')}
          >
            <span className="nav-btn-content">
              <FiBox className="nav-icon" /> Products Catalog
            </span>
          </button>
          <button
            className={`nav-link-btn ${activeTab === 'blogs' ? 'active' : ''}`}
            onClick={() => setActiveTab('blogs')}
          >
            <span className="nav-btn-content">
              <FiFileText className="nav-icon" /> Knowledge Blog
            </span>
          </button>
          <button
            className={`nav-link-btn ${activeTab === 'orders' ? 'active' : ''}`}
            onClick={() => setActiveTab('orders')}
          >
            <span className="nav-btn-content">
              <FiShoppingCart className="nav-icon" /> Purchase Orders
            </span>
            {pendingOrders > 0 && <span className="badge-alert">{pendingOrders}</span>}
          </button>
          <button
            className={`nav-link-btn ${activeTab === 'users' ? 'active' : ''}`}
            onClick={() => setActiveTab('users')}
          >
            <span className="nav-btn-content">
              <FiUsers className="nav-icon" /> User Management
            </span>
          </button>
          <button
            className={`nav-link-btn ${activeTab === 'logs' ? 'active' : ''}`}
            onClick={() => setActiveTab('logs')}
          >
            <span className="nav-btn-content">
              <FiShield className="nav-icon" /> System Audit Logs
            </span>
          </button>
          <button
            className={`nav-link-btn ${activeTab === 'landing' ? 'active' : ''}`}
            onClick={() => setActiveTab('landing')}
          >
            <span className="nav-btn-content">
              <FiHome className="nav-icon" /> Storefront Designer
            </span>
          </button>
          <button
            className={`nav-link-btn ${activeTab === 'settings' ? 'active' : ''}`}
            onClick={() => setActiveTab('settings')}
          >
            <span className="nav-btn-content">
              <FiSettings className="nav-icon" /> Store Settings
            </span>
          </button>
        </nav>

        <div className="sidebar-footer">
          <div className={`admin-profile-container ${profileDropdownOpen ? 'open' : ''}`}>
            <div className="admin-profile-row" onClick={() => setProfileDropdownOpen(!profileDropdownOpen)}>
              <span className="profile-initials">
                {currentUser ? currentUser.first_name[0] + currentUser.last_name[0] : 'MA'}
              </span>
              <div className="profile-details">
                <h4>{currentUser ? `${currentUser.first_name} ${currentUser.last_name}` : 'Master Admin'}</h4>
                <p>{currentUser ? currentUser.email : 'admin@orbifertorganic.com'}</p>
              </div>
              <span className="profile-chevron">{profileDropdownOpen ? <FiChevronUp className="profile-chevron-icon" /> : <FiChevronDown className="profile-chevron-icon" />}</span>
            </div>
            {profileDropdownOpen && (
              <div className="profile-popover-menu">
                <button className="popover-item" onClick={() => { setProfileDropdownOpen(false); onBack(); }}>
                  <FiHome className="popover-icon" /> Return to Storefront
                </button>
                <div className="popover-divider"></div>
                <button className="popover-item logout" onClick={() => { setProfileDropdownOpen(false); onLogout(); }}>
                  <FiLogOut className="popover-icon" /> Log Out Account
                </button>
              </div>
            )}
          </div>
        </div>
      </aside>

      {/* Main Administrative Dashboard View */}
      <main className="admin-main-viewport">
        <header className="viewport-header">
          <div className="header-meta">
            <h1>{activeTab.charAt(0).toUpperCase() + activeTab.slice(1)} Manager</h1>
            <p>Real-time analytics and controls dashboard.</p>
          </div>
          <div className="header-actions">
            <button className="refresh-sync-btn" onClick={fetchData} disabled={loading}>
              <FiRefreshCw className={`btn-sync-icon ${loading ? 'spin' : ''}`} /> {loading ? 'Synchronizing...' : 'Sync Data'}
            </button>
          </div>
        </header>

        <section className="viewport-body">
          {/* OVERVIEW PANEL */}
          {activeTab === 'overview' && (
            <div className="overview-tab-content animate-fade-in">
              <div className="stats-grid">
                <div className="stat-glass-card">
                  <span className="card-icon-wrapper rev"><FiTrendingUp /></span>
                  <div className="card-info">
                    <span>Delivered Revenue</span>
                    <h3>₹{totalRevenue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</h3>
                  </div>
                </div>
                <div className="stat-glass-card">
                  <span className="card-icon-wrapper prod"><FiBox /></span>
                  <div className="card-info">
                    <span>Active Catalog Products</span>
                    <h3>{products.length} Products</h3>
                  </div>
                </div>
                <div className="stat-glass-card">
                  <span className="card-icon-wrapper order"><FiShoppingCart /></span>
                  <div className="card-info">
                    <span>Purchase Orders</span>
                    <h3>{orders.length} Orders ({pendingOrders} Pending)</h3>
                  </div>
                </div>
                <div className="stat-glass-card">
                  <span className="card-icon-wrapper user"><FiUsers /></span>
                  <div className="card-info">
                    <span>Registered Users</span>
                    <h3>{users.length} Users</h3>
                  </div>
                </div>
              </div>

              <div className="overview-row-split">
                {/* Recent activity audit log snippet */}
                <div className="recent-activity-panel glass-block">
                  <div className="panel-header-row">
                    <h3>Recent System Audit Logs</h3>
                    <button className="link-action-btn" onClick={() => setActiveTab('logs')}>View All Logs →</button>
                  </div>
                  <div className="logs-timeline">
                    {logs.slice(0, 5).map((log) => (
                      <div key={log.id} className="timeline-item">
                        <div className="item-meta">
                          <span className="badge-action-type">{log.action}</span>
                          <span className="timestamp">{new Date(log.created_at).toLocaleString()}</span>
                        </div>
                        <p>{log.description}</p>
                        <span className="admin-email">Actor: {log.admin_email || 'System'}</span>
                      </div>
                    ))}
                    {logs.length === 0 && <p className="empty-text">No recent log history available.</p>}
                  </div>
                </div>

                {/* Quick Store Health Summary */}
                <div className="store-summary-panel glass-block">
                  <h3>Catalog & Content Health</h3>
                  <ul className="summary-health-list">
                    <li>
                      <span className="label">Category Spread:</span>
                      <span className="value">3 Active Families</span>
                    </li>
                    <li>
                      <span className="label">Blogging Content:</span>
                      <span className="value">{blogs.length} Articles published</span>
                    </li>
                    <li>
                      <span className="label">Database Mode:</span>
                      <span className="value mode-live">LIVE MySQL connected</span>
                    </li>
                    <li>
                      <span className="label">Server Status:</span>
                      <span className="value status-online">Online</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          )}

          {/* PRODUCTS TAB */}
          {activeTab === 'products' && (
            <div className="products-tab-content animate-fade-in">
              <div className="actions-bar-row">
                <h3>Storefront Catalog Listings ({products.length})</h3>
                <button className="admin-add-btn" onClick={handleOpenAddProduct}>
                  <FiPlus style={{ marginRight: '6px', verticalAlign: 'middle', fontSize: '15px' }} /> Publish New Product
                </button>
              </div>

              <div className="table-responsive-wrapper">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Product Info</th>
                      <th>SKU</th>
                      <th>Base Price</th>
                      <th>Stock Quantity</th>
                      <th>Category</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {products.map((p) => {
                      const baseVariant = p.variants?.[0] || {};
                      const startPrice = p.starting_price || baseVariant.price || '0.00';
                      const stockQty = baseVariant.stock_quantity !== undefined ? baseVariant.stock_quantity : 'N/A';

                      return (
                        <tr key={p.id}>
                          <td>#{p.id}</td>
                          <td>
                            <div className="row-product-profile">
                              <img src={(p.images && p.images.length > 0) ? p.images[0] : (p.image || "/products/Super Novel.png")} alt={p.title} className="prod-table-avatar" />
                              <div className="profile-desc">
                                <strong>{p.title}</strong>
                                <span>{p.short_description || 'No summary description provided.'}</span>
                              </div>
                            </div>
                          </td>
                          <td><code>{p.sku}</code></td>
                          <td><strong>₹{parseFloat(startPrice).toFixed(2)}</strong></td>
                          <td>
                            <span className={`stock-status-badge ${stockQty > 20 ? 'in' : stockQty > 0 ? 'low' : 'out'}`}>
                              {stockQty} Units
                            </span>
                          </td>
                          <td><span className="category-tag-badge">{p.category_name || 'General'}</span></td>
                          <td>
                            <div className="row-actions-group">
                              <button className="row-btn-edit" onClick={() => handleOpenEditProduct(p)}>
                                <FiEdit2 className="table-btn-icon" /> Edit
                              </button>
                              <button className="row-btn-delete" onClick={() => handleDeleteProduct(p.id)}>
                                <FiTrash2 className="table-btn-icon" /> Delete
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                    {products.length === 0 && (
                      <tr>
                        <td colSpan="7" className="table-empty-row">No active catalog listings found.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* BLOGS TAB */}
          {activeTab === 'blogs' && (
            <div className="blogs-tab-content animate-fade-in">
              <div className="actions-bar-row">
                <h3>Knowledge Hub Publications ({blogs.length})</h3>
                <button className="admin-add-btn" onClick={handleOpenAddBlog}>
                  <FiPlus style={{ marginRight: '6px', verticalAlign: 'middle', fontSize: '15px' }} /> Write New Article
                </button>
              </div>

              <div className="table-responsive-wrapper">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Article Info</th>
                      <th>Category</th>
                      <th>Publish Date</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {blogs.map((b) => (
                      <tr key={b.id}>
                        <td>#{b.id}</td>
                        <td>
                          <div className="row-product-profile">
                            <img src={b.image_url || "https://images.unsplash.com/photo-1592982537447-6f2a6a0c7c18?w=100&auto=format&fit=crop&q=80"} alt={b.title} className="prod-table-avatar" />
                            <div className="profile-desc">
                              <strong>{b.title}</strong>
                              <span>{b.summary || 'No summary overview details provided.'}</span>
                            </div>
                          </div>
                        </td>
                        <td><span className="category-tag-badge font-blog">{b.category}</span></td>
                        <td>{b.created_at ? new Date(b.created_at).toLocaleDateString() : 'N/A'}</td>
                        <td>
                          <div className="row-actions-group">
                            <button className="row-btn-edit" onClick={() => handleOpenEditBlog(b)}>
                              <FiEdit2 className="table-btn-icon" /> Edit
                            </button>
                            <button className="row-btn-delete" onClick={() => handleDeleteBlog(b.id)}>
                              <FiTrash2 className="table-btn-icon" /> Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                    {blogs.length === 0 && (
                      <tr>
                        <td colSpan="5" className="table-empty-row">No published blog articles found.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* ORDERS TAB */}
          {activeTab === 'orders' && (() => {
            const filteredOrders = orders.filter(o => {
              const q = orderSearchQuery.toLowerCase().trim();
              if (!q) return true;
              return (
                o.order_number.toLowerCase().includes(q) ||
                String(o.id).includes(q) ||
                (o.user_email && o.user_email.toLowerCase().includes(q))
              );
            });

            return (
              <div className="orders-tab-content animate-fade-in">
                <div className="actions-bar-row">
                  <h3>Customer Transactions History ({filteredOrders.length})</h3>
                  <div className="admin-search-box">
                    <input
                      type="text"
                      placeholder="Search Order ID or Email..."
                      value={orderSearchQuery}
                      onChange={(e) => setOrderSearchQuery(e.target.value)}
                      className="admin-search-input"
                    />
                  </div>
                </div>

                <div className="table-responsive-wrapper">
                  <table className="admin-table">
                    <thead>
                      <tr>
                        <th>Order No</th>
                        <th>User Account</th>
                        <th>Date</th>
                        <th>Amount</th>
                        <th>Gateway</th>
                        <th>Status &amp; Progress</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredOrders.map((o) => (
                        <tr key={o.id}>
                          <td><code>{o.order_number}</code></td>
                          <td>
                            <div className="user-email-col">
                              <strong>{o.user_email || 'Customer ID: ' + o.user_id}</strong>
                            </div>
                          </td>
                          <td>{new Date(o.created_at).toLocaleDateString()}</td>
                          <td><strong>₹{parseFloat(o.total_amount).toFixed(2)}</strong></td>
                          <td>
                            <span className="payment-method-pill">{o.payment_method || 'COD'}</span>
                          </td>
                          <td>
                            <span className={`order-status-badge ${o.status}`}>
                              {o.status.charAt(0).toUpperCase() + o.status.slice(1)}
                            </span>
                            {o.packaging_status && (
                              <span className="order-pkg-status-badge">
                                📦 {o.packaging_status}
                              </span>
                            )}
                          </td>
                          <td>
                            <button className="view-items-trigger" onClick={() => {
                              setSelectedAdminOrder(o);
                            }}>
                              <FiEye className="table-btn-icon" /> Manage Order
                            </button>
                          </td>
                        </tr>
                      ))}
                      {filteredOrders.length === 0 && (
                        <tr>
                          <td colSpan="7" className="table-empty-row">No orders matching search filter.</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })()}

          {/* USERS TAB */}
          {activeTab === 'users' && (
            <div className="users-tab-content animate-fade-in">
              <h3>Registered Customers and Staff Roles</h3>

              <div className="table-responsive-wrapper">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>User ID</th>
                      <th>Full Name</th>
                      <th>Email Address</th>
                      <th>Mobile Number</th>
                      <th>Access Roles</th>
                      <th>Account Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((u) => (
                      <tr key={u.id} className={u.is_active ? '' : 'user-row-deactivated'}>
                        <td>#{u.id}</td>
                        <td>{u.first_name} {u.last_name}</td>
                        <td><code>{u.email}</code></td>
                        <td>{u.phone_number || 'Not provided'}</td>
                        <td>
                          <select
                            value={u.roles?.[0] || 'Customer'}
                            className="admin-role-select"
                            onChange={(e) => handleRoleChange(u.id, e.target.value)}
                            disabled={currentUser && currentUser.id === u.id} // Prevents self-demotion
                          >
                            <option value="Customer">Customer</option>
                            <option value="Editor">Editor</option>
                            <option value="Sales Manager">Sales Manager</option>
                            <option value="Master Admin">Master Admin</option>
                          </select>
                        </td>
                        <td>
                          <button
                            className={`toggle-status-btn ${u.is_active ? 'active' : 'banned'}`}
                            onClick={() => handleToggleUserStatus(u.id, u.is_active)}
                            disabled={currentUser && currentUser.id === u.id} // Prevents locking self out
                          >
                            {u.is_active ? (
                              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}><FiCheckCircle /> Enabled</span>
                            ) : (
                              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}><FiXCircle /> Blocked</span>
                            )}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* LOGS TAB */}
          {activeTab === 'logs' && (
            <div className="logs-tab-content animate-fade-in">
              <h3>Audit Trails & Security Activity Registry</h3>
              <div className="table-responsive-wrapper max-height-audit">
                <table className="admin-table sticky-header">
                  <thead>
                    <tr>
                      <th>Time</th>
                      <th>Action</th>
                      <th>Entity</th>
                      <th>Record ID</th>
                      <th>Audit Details</th>
                      <th>Admin Email</th>
                    </tr>
                  </thead>
                  <tbody>
                    {logs.map((log) => (
                      <tr key={log.id}>
                        <td><small>{new Date(log.created_at).toLocaleString()}</small></td>
                        <td><span className="log-action-badge">{log.action}</span></td>
                        <td><code>{log.target_table}</code></td>
                        <td>#{log.target_id}</td>
                        <td>{log.description}</td>
                        <td><code>{log.admin_email || 'System'}</code></td>
                      </tr>
                    ))}
                    {logs.length === 0 && (
                      <tr>
                        <td colSpan="6" className="table-empty-row">No audit log registries found.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* STOREFRONT DESIGNER PANEL */}
          {activeTab === 'landing' && (
            <div className="overview-tab-content animate-fade-in">
              <div className="glass-block" style={{ maxWidth: '100%', margin: '0 auto' }}>
                <div className="panel-header-row" style={{ borderBottom: '1px solid #e2e8f0', paddingBottom: '12px', marginBottom: '20px' }}>
                  <h3>Storefront Layout Designer</h3>
                </div>
                <p style={{ fontSize: '13px', color: '#64748b', marginBottom: '20px', lineHeight: '1.5' }}>
                  Design the public landing page layout. Select which catalog products are showcased in the <strong>Top Products</strong> section and the <strong>Product Spotlight</strong> section.
                </p>

                <form onSubmit={handleLandingDesignerSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                  
                  {/* Top Products section */}
                  <div>
                    <h4 style={{ fontSize: '14px', fontWeight: '700', color: '#0f172a', borderBottom: '1px solid #f1f5f9', paddingBottom: '6px', marginBottom: '16px' }}>
                      Top Products Row (Select exactly 4 products)
                    </h4>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
                      {[0, 1, 2, 3].map((slotIndex) => {
                        const productList = products.length > 0 ? products : fallbackProducts;
                        return (
                          <div key={slotIndex} className="form-group">
                            <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#475569', marginBottom: '6px' }}>
                              Slot {slotIndex + 1} Product
                            </label>
                            <select
                              value={landingForm.topProducts[slotIndex] || ''}
                              onChange={(e) => handleTopProductChange(slotIndex, e.target.value)}
                              required
                              style={{ width: '100%', padding: '10px 12px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '14px', background: '#f8fafc' }}
                            >
                              <option value="" disabled>Select a product...</option>
                              {productList.map((p) => (
                                <option key={p.id} value={p.id}>
                                  {p.title} (ID #{p.id})
                                </option>
                              ))}
                            </select>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Spotlight section */}
                  <div>
                    <h4 style={{ fontSize: '14px', fontWeight: '700', color: '#0f172a', borderBottom: '1px solid #f1f5f9', paddingBottom: '6px', marginBottom: '16px' }}>
                      Product Spotlight Row (Select exactly 3 products)
                    </h4>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
                      {[0, 1, 2].map((slotIndex) => {
                        const productList = products.length > 0 ? products : fallbackProducts;
                        const isHighlight = slotIndex === 1;
                        return (
                          <div key={slotIndex} className="form-group">
                            <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#475569', marginBottom: '6px' }}>
                              Slot {slotIndex + 1} Product {isHighlight ? '(Highlighted Center)' : ''}
                            </label>
                            <select
                              value={landingForm.spotlightProducts[slotIndex] || ''}
                              onChange={(e) => handleSpotlightProductChange(slotIndex, e.target.value)}
                              required
                              style={{
                                width: '100%',
                                padding: '10px 12px',
                                border: isHighlight ? '2px solid #00B207' : '1px solid #cbd5e1',
                                borderRadius: '6px',
                                fontSize: '14px',
                                background: isHighlight ? '#f0fdf4' : '#f8fafc'
                              }}
                            >
                              <option value="" disabled>Select a product...</option>
                              {productList.map((p) => (
                                <option key={p.id} value={p.id}>
                                  {p.title} (ID #{p.id})
                                </option>
                              ))}
                            </select>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  <div style={{ marginTop: '12px' }}>
                    <button
                      type="submit"
                      className="admin-add-btn"
                      style={{ width: '100%', padding: '12px', fontSize: '14px' }}
                    >
                      Save Storefront Layout Selections
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* SETTINGS PANEL */}
          {activeTab === 'settings' && (
            <div className="overview-tab-content animate-fade-in">
              <div className="glass-block" style={{ maxWidth: '100%', margin: '0 auto' }}>
                <div className="panel-header-row" style={{ borderBottom: '1px solid #e2e8f0', paddingBottom: '12px', marginBottom: '20px' }}>
                  <h3>Store Settings Configuration</h3>
                </div>
                <p style={{ fontSize: '13px', color: '#64748b', marginBottom: '20px', lineHeight: '1.5' }}>
                  Configure basic storefront parameters, such as default shipping costs, free shipping minimum thresholds, and standard GST/tax percentage rates.
                </p>

                {/* Sub-tab Selection */}
                <div style={{ display: 'flex', borderBottom: '2px solid #e2e8f0', marginBottom: '24px', gap: '8px' }}>
                  <button
                    type="button"
                    onClick={() => setSettingsSubTab('store')}
                    style={{
                      padding: '10px 16px',
                      fontSize: '14px',
                      fontWeight: '600',
                      background: 'none',
                      border: 'none',
                      borderBottom: settingsSubTab === 'store' ? '3px solid #00B207' : '3px solid transparent',
                      color: settingsSubTab === 'store' ? '#00B207' : '#64748b',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      outline: 'none'
                    }}
                  >
                    ⚙️ Store Settings
                  </button>
                  <button
                    type="button"
                    onClick={() => setSettingsSubTab('invoice')}
                    style={{
                      padding: '10px 16px',
                      fontSize: '14px',
                      fontWeight: '600',
                      background: 'none',
                      border: 'none',
                      borderBottom: settingsSubTab === 'invoice' ? '3px solid #00B207' : '3px solid transparent',
                      color: settingsSubTab === 'invoice' ? '#00B207' : '#64748b',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      outline: 'none'
                    }}
                  >
                    📄 Tax Invoice Config
                  </button>
                </div>

                <form onSubmit={handleSettingsSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>

                  {settingsSubTab === 'store' && (
                    <div className="animate-fade-in" style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                      <h4 style={{ fontSize: '14px', fontWeight: '700', color: '#0f172a', borderBottom: '1px solid #f1f5f9', paddingBottom: '6px', marginTop: '10px' }}>
                        Storefront Pricing Parameters
                      </h4>

                      <div className="form-group">
                        <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#475569', marginBottom: '6px' }}>Default Shipping Charge (₹)</label>
                        <input
                          type="number"
                          step="0.01"
                          value={settingsForm.shipping_fee}
                          onChange={(e) => setSettingsForm({ ...settingsForm, shipping_fee: e.target.value })}
                          placeholder="e.g. 50"
                          required
                          style={{ width: '100%', padding: '10px 12px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '14px', background: '#f8fafc' }}
                        />
                      </div>

                      <div className="form-group">
                        <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#475569', marginBottom: '6px' }}>Free Shipping Threshold (₹)</label>
                        <input
                          type="number"
                          step="0.01"
                          value={settingsForm.free_shipping_threshold}
                          onChange={(e) => setSettingsForm({ ...settingsForm, free_shipping_threshold: e.target.value })}
                          placeholder="e.g. 500"
                          required
                          style={{ width: '100%', padding: '10px 12px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '14px', background: '#f8fafc' }}
                        />
                      </div>

                      <div className="form-group">
                        <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#475569', marginBottom: '6px' }}>Standard Tax/GST Rate (%)</label>
                        <input
                          type="number"
                          step="0.1"
                          value={settingsForm.tax_rate}
                          onChange={(e) => setSettingsForm({ ...settingsForm, tax_rate: e.target.value })}
                          placeholder="e.g. 5"
                          required
                          style={{ width: '100%', padding: '10px 12px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '14px', background: '#f8fafc' }}
                        />
                      </div>
                    </div>
                  )}

                  {settingsSubTab === 'invoice' && (
                    <div className="animate-fade-in" style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                      <h4 style={{ fontSize: '14px', fontWeight: '700', color: '#0f172a', borderBottom: '1px solid #f1f5f9', paddingBottom: '6px', marginTop: '10px' }}>
                        Tax Invoice Config (Billed From)
                      </h4>

                      <div className="form-group">
                        <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#475569', marginBottom: '6px' }}>Company Registered Name</label>
                        <input
                          type="text"
                          value={settingsForm.invoice_company_name}
                          onChange={(e) => setSettingsForm({ ...settingsForm, invoice_company_name: e.target.value })}
                          placeholder="e.g. Orbifert Organic Pvt. Ltd."
                          required
                          style={{ width: '100%', padding: '10px 12px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '14px', background: '#f8fafc' }}
                        />
                      </div>

                      <div className="form-group">
                        <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#475569', marginBottom: '6px' }}>Registered Office Address</label>
                        <textarea
                          value={settingsForm.invoice_company_address}
                          onChange={(e) => setSettingsForm({ ...settingsForm, invoice_company_address: e.target.value })}
                          placeholder="Enter full address details"
                          required
                          rows="3"
                          style={{ width: '100%', padding: '10px 12px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '14px', background: '#f8fafc', fontFamily: 'inherit', resize: 'vertical' }}
                        />
                      </div>

                      <div className="form-group">
                        <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#475569', marginBottom: '6px' }}>GSTIN Number</label>
                        <input
                          type="text"
                          value={settingsForm.invoice_gstin}
                          onChange={(e) => setSettingsForm({ ...settingsForm, invoice_gstin: e.target.value })}
                          placeholder="e.g. 29AABCF8078M1ZX"
                          required
                          style={{ width: '100%', padding: '10px 12px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '14px', background: '#f8fafc' }}
                        />
                      </div>

                      <div className="form-group">
                        <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#475569', marginBottom: '6px' }}>PAN Number</label>
                        <input
                          type="text"
                          value={settingsForm.invoice_pan}
                          onChange={(e) => setSettingsForm({ ...settingsForm, invoice_pan: e.target.value })}
                          placeholder="e.g. AABCF8078M"
                          required
                          style={{ width: '100%', padding: '10px 12px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '14px', background: '#f8fafc' }}
                        />
                      </div>

                      <div className="form-group">
                        <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#475569', marginBottom: '6px' }}>CIN Number</label>
                        <input
                          type="text"
                          value={settingsForm.invoice_cin}
                          onChange={(e) => setSettingsForm({ ...settingsForm, invoice_cin: e.target.value })}
                          placeholder="e.g. U51909KA2011PTC060489"
                          required
                          style={{ width: '100%', padding: '10px 12px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '14px', background: '#f8fafc' }}
                        />
                      </div>

                      <div className="form-group">
                        <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#475569', marginBottom: '6px' }}>Support Contact Phone</label>
                        <input
                          type="text"
                          value={settingsForm.invoice_contact_phone}
                          onChange={(e) => setSettingsForm({ ...settingsForm, invoice_contact_phone: e.target.value })}
                          placeholder="e.g. 080-67302000"
                          required
                          style={{ width: '100%', padding: '10px 12px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '14px', background: '#f8fafc' }}
                        />
                      </div>

                      <div className="form-group">
                        <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#475569', marginBottom: '6px' }}>Support Contact Email</label>
                        <input
                          type="email"
                          value={settingsForm.invoice_contact_email}
                          onChange={(e) => setSettingsForm({ ...settingsForm, invoice_contact_email: e.target.value })}
                          placeholder="e.g. support@orbifertorganic.com"
                          required
                          style={{ width: '100%', padding: '10px 12px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '14px', background: '#f8fafc' }}
                        />
                      </div>
                    </div>
                  )}

                  <div style={{ marginTop: '16px' }}>
                    <button
                      type="submit"
                      className="admin-add-btn"
                      style={{ width: '100%', padding: '12px', fontSize: '14px' }}
                    >
                      Save Settings Configuration
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}
        </section>
      </main>

      {/* Extracted Product Form Modal */}
      <ProductFormModal
        isOpen={productFormOpen}
        onClose={() => setProductFormOpen(false)}
        editingProduct={editingProduct}
        categories={categories}
        onCategoryAdded={(newCat) => setCategories(prev => [...prev, newCat])}
        API_URL={API_URL}
        token={token}
        triggerNotification={triggerNotification}
        fetchData={fetchData}
      />

      {/* Extracted Blog Form Modal */}
      <BlogFormModal
        isOpen={blogFormOpen}
        onClose={() => setBlogFormOpen(false)}
        editingBlog={editingBlog}
        API_URL={API_URL}
        token={token}
        triggerNotification={triggerNotification}
        fetchData={fetchData}
      />

      {/* Extracted Order Items Modal */}
      <OrderItemsModal
        isOpen={!!selectedOrderItems}
        onClose={() => setSelectedOrderItems(null)}
        items={selectedOrderItems}
      />

      {/* Extracted Order Details Modal */}
      <OrderDetailsModal
        isOpen={!!selectedAdminOrder}
        onClose={() => setSelectedAdminOrder(null)}
        order={selectedAdminOrder}
        onUpdateOrderDetails={handleUpdateOrderDetails}
        onViewItems={(items) => setSelectedOrderItems(items)}
      />
    </div>
  );
}
